/**
 * Contract Template Utilities
 *
 * Variable interpolation, formatting, and template manipulation utilities
 */

import type {
  ContractVariableData,
  ContractVariableName,
  VariableFormatter
} from './contract-types';

// ============================================================================
// Variable Formatters
// ============================================================================

/**
 * Format a value as currency
 */
const formatCurrency = (value: any): string => {
  const num = parseFloat(value);
  if (isNaN(num)) {
return value?.toString() || '';
}
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

/**
 * Format a value as percentage
 */
const formatPercentage = (value: any): string => {
  const num = parseFloat(value);
  if (isNaN(num)) {
return value?.toString() || '';
}
  return `${num}%`;
};

/**
 * Format a value as basis points
 */
const formatBps = (value: any): string => {
  const num = parseFloat(value);
  if (isNaN(num)) {
return value?.toString() || '';
}
  return `${num} bps`;
};

/**
 * Format a phone number
 */
const formatPhone = (value: any): string => {
  if (!value) {
return '';
}
  const cleaned = value.toString().replace(/\D/g, '');

  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length === 11) {
    return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return value.toString();
};

/**
 * Format a date as long format (e.g., "January 1, 2025")
 */
const formatDate = (value: any): string => {
  if (!value) {
return '';
}

  const date = new Date(value);
  if (isNaN(date.getTime())) {
return value.toString();
}

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(date);
};

/**
 * Format a date as short format (e.g., "01/01/2025")
 */
const formatDateShort = (value: any): string => {
  if (!value) {
return '';
}

  const date = new Date(value);
  if (isNaN(date.getTime())) {
return value.toString();
}

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

/**
 * Format an array as comma-separated list
 */
const formatList = (value: any): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return value?.toString() || '';
};

/**
 * Convert to uppercase
 */
const formatUpper = (value: any): string => {
  return value?.toString().toUpperCase() || '';
};

/**
 * Convert to lowercase
 */
const formatLower = (value: any): string => {
  return value?.toString().toLowerCase() || '';
};

/**
 * Convert to title case
 */
const formatTitle = (value: any): string => {
  if (!value) {
return '';
}
  return value
    .toString()
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Formatter function registry
 */
const FORMATTERS: Record<VariableFormatter, (value: any) => string> = {
  currency: formatCurrency,
  percentage: formatPercentage,
  bps: formatBps,
  phone: formatPhone,
  date: formatDate,
  dateShort: formatDateShort,
  list: formatList,
  upper: formatUpper,
  lower: formatLower,
  title: formatTitle
};

// ============================================================================
// Variable Interpolation
// ============================================================================

/**
 * Interpolate variables in a text string
 *
 * Supports formats:
 * - {{variableName}} - Simple replacement
 * - {{variableName:formatter}} - Formatted replacement
 *
 * @param template Template string with variable placeholders
 * @param variables Variable data
 * @param options Interpolation options
 * @returns Interpolated string
 */
export function interpolateVariables(
  template: string,
  variables: ContractVariableData,
  options: {
    fallback?: string;
    strict?: boolean;
  } = {}
): string {
  const { fallback = '', strict = false } = options;

  return template.replace(
    /\{\{(\w+)(?::(\w+))?\}\}/g,
    (match, varName: ContractVariableName, formatter?: VariableFormatter) => {
      const value = variables[varName];

      // Handle missing variables
      if (value === undefined || value === null || value === '') {
        // Special placeholder for contractDate - will be set when contract is signed
        if (varName === 'contractDate') {
          return '[Date to be determined upon signing]';
        }

        if (strict) {
          throw new Error(`Missing required variable: ${varName}`);
        }
        return fallback;
      }

      // Apply formatter if specified
      if (formatter && FORMATTERS[formatter]) {
        try {
          return FORMATTERS[formatter](value);
        } catch (error) {
          console.error(`Error formatting variable ${varName} with formatter ${formatter}:`, error);
          return String(value);
        }
      }

      // Return raw value
      return String(value);
    }
  );
}

/**
 * Extract all variable names from a template string
 *
 * @param template Template string
 * @returns Array of unique variable names found in template
 */
export function extractVariableNames(template: string): ContractVariableName[] {
  const matches = template.matchAll(/\{\{(\w+)(?::(\w+))?\}\}/g);
  const names = new Set<ContractVariableName>();

  for (const match of matches) {
    names.add(match[1] as ContractVariableName);
  }

  return Array.from(names);
}

/**
 * Validate that all required variables are present in data
 *
 * @param requiredVars Required variable names
 * @param variables Variable data
 * @returns Array of missing variable names
 */
export function validateRequiredVariables(
  requiredVars: ContractVariableName[],
  variables: ContractVariableData
): ContractVariableName[] {
  const missing: ContractVariableName[] = [];

  for (const varName of requiredVars) {
    const value = variables[varName];
    if (value === undefined || value === null || value === '') {
      missing.push(varName);
    }
  }

  return missing;
}

// ============================================================================
// Derived Variables
// ============================================================================

/**
 * Compute derived variables from base data
 *
 * Some variables are computed from others (e.g., fullName from firstName + lastName)
 *
 * @param baseData Base variable data
 * @returns Complete variable data with derived values
 */
export function computeDerivedVariables(
  baseData: ContractVariableData
): ContractVariableData {
  const derived: ContractVariableData = { ...baseData };

  // Full name
  if (baseData.firstName && baseData.lastName) {
    derived.fullName = `${baseData.firstName} ${baseData.lastName}`;
  }

  // Full mailing address
  if (baseData.mailingStreetAddress || baseData.mailingCity || baseData.mailingState || baseData.mailingZip) {
    const parts: string[] = [];

    if (baseData.mailingStreetAddress) {
      parts.push(baseData.mailingStreetAddress);
    }

    const cityStateZip: string[] = [];
    if (baseData.mailingCity) {
cityStateZip.push(baseData.mailingCity);
}
    if (baseData.mailingState) {
cityStateZip.push(baseData.mailingState);
}
    if (baseData.mailingZip) {
cityStateZip.push(baseData.mailingZip);
}

    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(', '));
    }

    derived.fullMailingAddress = parts.join('\n');
  }

  // Contract date is NOT auto-generated - will be set when contract is signed
  // Leave as undefined if not provided

  // Company defaults
  if (!derived.companyName) {
    derived.companyName = 'Lendwise Financial';
  }

  if (!derived.companyAddress) {
    derived.companyAddress = '123 Main Street, Los Angeles, CA 90001';
  }

  // Position defaults to "Loan Officer"
  if (!derived.position) {
    derived.position = 'Loan Officer';
  }

  // Calculate loan officer's split in mentorship program (complement of mentor split)
  if (baseData.brokerMentorCommissionSplit !== undefined && baseData.brokerMentorCommissionSplit !== null) {
    derived.loanOfficerMentorSplit = 100 - baseData.brokerMentorCommissionSplit;
  }

  // Mentorship example calculations (based on $10,000 commission)
  if (baseData.brokerCommissionSplit !== undefined && baseData.brokerCommissionSplit !== null) {
    const loAllocation = 10000 * (baseData.brokerCommissionSplit / 100);
    derived.mentorshipLoAllocation = loAllocation;

    if (baseData.loanOfficerMentorSplit !== undefined) {
      derived.mentorshipLoReceives = loAllocation * (baseData.loanOfficerMentorSplit / 100);
    }

    if (baseData.brokerMentorCommissionSplit !== undefined) {
      derived.mentorshipMentorReceives = loAllocation * (baseData.brokerMentorCommissionSplit / 100);
    }

    derived.mentorshipCompanyRetains = 10000 - loAllocation;
  }

  // Banked compensation example calculation (based on $500,000 loan)
  if (baseData.bankedCompensation !== undefined && baseData.bankedCompensation !== null) {
    derived.bankedCompensationExample = 500000 * (baseData.bankedCompensation / 100);
  }

  return derived;
}

// ============================================================================
// Template Utilities
// ============================================================================

/**
 * Generate a unique ID for contract sections/content
 */
export function generateId(prefix: string = 'item'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Clone a template deeply
 */
export function cloneTemplate<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}
